<template>
    <div class="forgot-password-page">
        <div class="lang-switcher">
            <LangSwitcher />
        </div>

        <div class="forgot-password-container">
            <div class="auth-header">
                <div class="brand-logo">
                    <i class="iconfont icon-shipin" />
                </div>
                <h2>{{ $t('Forgot.找回密码') }}</h2>
                <p v-if="!resetSuccess">{{ $t('Forgot.请输入您的邮箱，我们将发送验证码') }}</p>
                <p v-else>{{ $t('Forgot.密码重置成功，请使用新密码登录') }}</p>
            </div>

            <!-- 成功状态 -->
            <div v-if="resetSuccess" class="success-content">
                <div class="success-icon">
                    <i class="el-icon-check" />
                </div>
                <h3>{{ $t('Forgot.密码重置成功') }}</h3>
                <p>{{ $t('Forgot.您的密码已成功重置，请使用新密码登录') }}</p>
                <router-link to="/login" class="btn btn-primary">
                    {{ $t('Forgot.立即登录') }}
                </router-link>
            </div>

            <!-- 重置表单 -->
            <div v-else class="form-content">
                <form @submit.prevent="handleResetPassword">
                    <!-- 邮箱 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('Forgot.邮箱') }}</label>
                        <div class="email-input-wrapper">
                            <input v-model="form.email" type="email" class="form-input"
                                :class="{ 'error': errors.email }" :placeholder="$t('Forgot.请输入注册邮箱')" required
                                @blur="validateEmail" :disabled="sendingCode">
                            <button type="button" v-if="form.email && !errors.email" class="send-code-btn"
                                :disabled="codeCountdown > 0 || sendingCode" @click="sendResetCode">
                                {{ codeCountdown > 0 ? `${codeCountdown}s` : $t('Forgot.发送验证码') }}
                            </button>
                        </div>
                        <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                    </div>

                    <!-- 验证码 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('Forgot.验证码') }}</label>
                        <input v-model="form.verificationCode" type="text" class="form-input"
                            :class="{ 'error': errors.verificationCode }" :placeholder="$t('Forgot.请输入验证码')" required
                            @blur="validateCode" maxlength="6">
                        <span v-if="errors.verificationCode" class="error-message">{{ errors.verificationCode }}</span>
                    </div>

                    <!-- 新密码 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('Forgot.新密码') }}</label>
                        <div class="password-input-wrapper">
                            <input v-model="form.newPassword" :type="showNewPassword ? 'text' : 'password'"
                                class="form-input" :class="{ 'error': errors.newPassword }"
                                :placeholder="$t('Forgot.设置新密码')" required @blur="validateNewPassword">
                            <button type="button" class="password-toggle" @click="showNewPassword = !showNewPassword">
                                <i
                                    :class="showNewPassword ? 'iconfont icon-mimabukejian' : 'iconfont icon-mimakejian'" />
                            </button>
                        </div>
                        <span v-if="errors.newPassword" class="error-message">{{ errors.newPassword }}</span>
                        <div v-if="form.newPassword" class="password-strength">
                            <div class="strength-bar" :class="passwordStrength.class" />
                            <span class="strength-text">{{ passwordStrength.text }}</span>
                        </div>
                    </div>

                    <!-- 确认新密码 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('Forgot.确认新密码') }}</label>
                        <div class="password-input-wrapper">
                            <input v-model="form.confirmNewPassword" :type="showConfirmPassword ? 'text' : 'password'"
                                class="form-input" :class="{ 'error': errors.confirmPassword }"
                                :placeholder="$t('Forgot.再次输入新密码')" required @blur="validateConfirmPassword">
                            <button type="button" class="password-toggle"
                                @click="showConfirmPassword = !showConfirmPassword">
                                <i
                                    :class="showConfirmPassword ? 'iconfont icon-mimabukejian' : 'iconfont icon-mimakejian'" />
                            </button>
                        </div>
                        <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
                    </div>

                    <button type="submit" class="btn btn-primary" :disabled="loading || !isFormValid">
                        <i v-if="loading" class="fas fa-spinner fa-spin" />
                        {{ loading ? $t('Forgot.重置中') : $t('Forgot.重置密码') }}
                    </button>
                </form>
            </div>

            <div class="auth-footer">
                <router-link to="/login">
                    <i class="iconfont icon-fanhui" /> {{ $t('Forgot.返回登录') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { setSendEmailCodeApi, setAuthForgotApi } from '@/api/api'

export default {
    name: 'ForgotPasswordPage',
    data() {
        return {
            // 表单数据
            form: {
                email: '',
                verificationCode: '',
                newPassword: '',
                confirmNewPassword: ''
            },

            // 加载状态
            loading: false,
            sendingCode: false,
            codeCountdown: 0,
            resetSuccess: false,

            // 密码显示切换
            showNewPassword: false,
            showConfirmPassword: false,

            // 错误信息
            errors: {
                email: '',
                verificationCode: '',
                newPassword: '',
                confirmPassword: ''
            }
        }
    },

    computed: {
        isFormValid() {
            return this.form.email &&
                this.form.verificationCode &&
                this.form.newPassword &&
                this.form.confirmNewPassword &&
                !this.errors.email &&
                !this.errors.verificationCode &&
                !this.errors.newPassword &&
                !this.errors.confirmPassword &&
                this.form.newPassword === this.form.confirmNewPassword
        },

        passwordStrength() {
            if (!this.form.newPassword) return { class: '', text: '' }

            let strength = 0
            if (this.form.newPassword.length >= 6) strength++
            if (this.form.newPassword.length >= 8) strength++
            if (/[A-Z]/.test(this.form.newPassword)) strength++
            if (/[0-9]/.test(this.form.newPassword)) strength++
            if (/[^A-Za-z0-9]/.test(this.form.newPassword)) strength++

            if (strength <= 2) return { class: 'weak', text: this.$t('Forgot.弱') }
            if (strength <= 3) return { class: 'medium', text: this.$t('Forgot.中') }
            return { class: 'strong', text: this.$t('Forgot.强') }
        }
    },

    methods: {
        // 验证邮箱
        validateEmail() {
            if (!this.form.email.trim()) {
                this.errors.email = this.$t('Forgot.请输入邮箱地址')
            } else if (!/^[^\s@]+@([^\s@]+\.)+[^\s@]+$/.test(this.form.email)) {
                this.errors.email = this.$t('Forgot.请输入正确的邮箱格式')
            } else {
                this.errors.email = ''
            }
        },

        // 验证验证码
        validateCode() {
            if (!this.form.verificationCode) {
                this.errors.verificationCode = this.$t('Forgot.请输入验证码')
            } else if (!/^\d{6}$/.test(this.form.verificationCode)) {
                this.errors.verificationCode = this.$t('Forgot.验证码应为6位数字')
            } else {
                this.errors.verificationCode = ''
            }
        },

        // 验证新密码
        validateNewPassword() {
            if (!this.form.newPassword) {
                this.errors.newPassword = this.$t('Forgot.请输入新密码')
            } else if (this.form.newPassword.length < 6) {
                this.errors.newPassword = this.$t('Forgot.密码至少需要6位')
            } else {
                this.errors.newPassword = ''
            }

            if (this.form.confirmNewPassword) {
                this.validateConfirmPassword()
            }
        },

        // 验证确认密码
        validateConfirmPassword() {
            if (!this.form.confirmNewPassword) {
                this.errors.confirmPassword = this.$t('Forgot.请再次输入新密码')
            } else if (this.form.newPassword !== this.form.confirmNewPassword) {
                this.errors.confirmPassword = this.$t('Forgot.两次输入的密码不一致')
            } else {
                this.errors.confirmPassword = ''
            }
        },

        // 开始倒计时
        startCountdown() {
            this.codeCountdown = 60
            const timer = setInterval(() => {
                if (this.codeCountdown <= 1) {
                    clearInterval(timer)
                    this.codeCountdown = 0
                } else {
                    this.codeCountdown--
                }
            }, 1000)
        },

        // 发送验证码
        sendResetCode() {
            this.validateEmail()

            if (this.errors.email) {
                this.$message.warning(this.errors.email)
                return
            }

            this.sendingCode = true
            setSendEmailCodeApi({
                email: this.form.email,
                type: 'forgot'
            }).then(res => {
                this.startCountdown()
                if (res.data.code) {
                    this.form.verificationCode = res.data.code
                }
                this.$message.success(this.$t('Forgot.验证码已发送'))
            }).finally(() => {
                this.sendingCode = false
            }).catch(() => { })
        },

        // 重置密码
        handleResetPassword() {
            // 验证所有字段
            this.validateEmail()
            this.validateCode()
            this.validateNewPassword()
            this.validateConfirmPassword()

            this.loading = true
            setAuthForgotApi({
                email: this.form.email.trim(),
                password: this.form.newPassword,
                code: this.form.verificationCode
            }).then(() => {
                this.resetSuccess = true
                this.$message.success(this.$t('Forgot.密码重置成功'))
            }).finally(() => {
                this.loading = false
            }).catch(() => { })
        }
    }
}
</script>

<style lang="scss" scoped>
.forgot-password-page {
    min-height: 100vh;
    background: #08080f;
    color: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    position: relative;

    .lang-switcher {
        position: fixed;
        top: 1rem;
        right: 1rem;
        z-index: 100;
    }

    .forgot-password-container {
        width: 100%;
        max-width: 580px;
        background: rgba(255, 255, 255, 0.02);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        padding: 2.5rem;

        .auth-header {
            text-align: center;
            margin-bottom: 2rem;

            .brand-logo {
                width: 64px;
                height: 64px;
                background: linear-gradient(135deg, #6366f1, #a855f7);
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 2rem;
                color: white;
                margin: 0 auto 1.5rem;
            }

            h2 {
                font-size: 1.75rem;
                margin-bottom: 0.5rem;
            }

            p {
                color: #64748b;
            }
        }

        .form-content {
            .form-group {
                margin-bottom: 1.25rem;

                .form-label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                    color: #94a3b8;
                }

                .email-input-wrapper {
                    display: flex;
                    gap: 0.75rem;

                    .form-input {
                        flex: 1;
                    }

                    .send-code-btn {
                        padding: 0 1rem;
                        background: linear-gradient(135deg, #6366f1, #a855f7);
                        border: none;
                        border-radius: 10px;
                        color: white;
                        font-size: 0.875rem;
                        font-weight: 500;
                        cursor: pointer;
                        white-space: nowrap;
                        transition: all 0.3s;

                        &:hover:not(:disabled) {
                            opacity: 0.9;
                            transform: translateY(-1px);
                        }

                        &:disabled {
                            opacity: 0.5;
                            cursor: not-allowed;
                        }
                    }
                }

                .password-input-wrapper {
                    position: relative;

                    .form-input {
                        padding-right: 2.5rem;
                    }

                    .password-toggle {
                        position: absolute;
                        right: 0.75rem;
                        top: 50%;
                        transform: translateY(-50%);
                        background: none;
                        border: none;
                        color: #64748b;
                        cursor: pointer;
                        padding: 0;
                        font-size: 1rem;

                        &:hover {
                            color: #f1f5f9;
                        }
                    }
                }

                .form-input {
                    width: 100%;
                    padding: 0.875rem 1rem;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                    color: #f1f5f9;
                    font-size: 1rem;
                    transition: all 0.3s;

                    &:focus {
                        outline: none;
                        border-color: #6366f1;
                    }

                    &::placeholder {
                        color: #64748b;
                    }

                    &.error {
                        border-color: #ef4444;
                    }

                    &:disabled {
                        opacity: 0.6;
                    }
                }

                .error-message {
                    display: block;
                    font-size: 0.75rem;
                    color: #ef4444;
                    margin-top: 0.375rem;
                }

                .password-strength {
                    margin-top: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;

                    .strength-bar {
                        width: 60px;
                        height: 4px;
                        border-radius: 2px;
                        background: rgba(255, 255, 255, 0.1);

                        &.weak {
                            background: #ef4444;
                            width: 30px;
                        }

                        &.medium {
                            background: #f59e0b;
                            width: 60px;
                        }

                        &.strong {
                            background: #10b981;
                            width: 60px;
                        }
                    }

                    .strength-text {
                        font-size: 0.75rem;
                        color: #64748b;
                    }
                }
            }

            .btn {
                width: 100%;
                padding: 0.875rem;
                border-radius: 10px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                &.btn-primary {
                    background: linear-gradient(135deg, #6366f1, #a855f7);
                    color: white;

                    &:hover:not(:disabled) {
                        opacity: 0.9;
                        transform: translateY(-2px);
                    }
                }
            }
        }

        .success-content {
            text-align: center;

            .success-icon {
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, #10b981, #059669);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 auto 1.5rem;

                i {
                    font-size: 3rem;
                    color: white;
                }
            }

            h3 {
                font-size: 1.5rem;
                margin-bottom: 0.5rem;
            }

            p {
                color: #64748b;
                margin-bottom: 2rem;
            }

            .btn {
                display: inline-block;
                text-decoration: none;
                width: auto;
                padding: 0.875rem 2rem;
                border-radius: 10px;
                cursor: pointer;
                transition: all 0.3s;
                border: none;

                &.btn-primary {
                    background: linear-gradient(135deg, #6366f1, #a855f7);
                    color: white;

                    &:hover:not(:disabled) {
                        opacity: 0.9;
                        transform: translateY(-2px);
                    }
                }
            }
        }

        .auth-footer {
            text-align: center;
            margin-top: 1.5rem;
            padding-top: 1.5rem;
            border-top: 1px solid rgba(255, 255, 255, 0.08);

            a {
                color: #6366f1;
                text-decoration: none;
                font-weight: 600;
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;

                &:hover {
                    color: #818cf8;
                }

                i {
                    font-size: 0.875rem;
                }
            }
        }
    }
}

@media (max-width: 640px) {
    .forgot-password-page {
        padding: 1rem;

        .forgot-password-container {
            padding: 1.5rem;
            border: none;
        }
    }
}
</style>